import { useEffect, useRef, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";

/**
 * A button that opens a panel under it — the filter bar's pickers, the Export
 * menu and the table's Columns picker all use it. Closes on outside click and
 * Escape. `align="end"` anchors the panel to the button's end edge (flips in
 * RTL through logical properties).
 */
export default function Popover({
  label,
  icon: Icon,
  children,
  align = "start",
  buttonClassName = "",
  panelClassName = "w-64",
  disabled = false,
  ariaLabel,
  chevron = true,
  variant = "secondary",
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const onDown = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = () => setOpen(false);

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((value) => !value)}
        className={`flex min-h-control items-center gap-2 rounded-control border px-3 text-dense transition-colors duration-fast disabled:cursor-not-allowed disabled:text-ink-muted ${
          variant === "primary"
            ? "border-accent bg-accent font-bold text-accent-on hover:bg-accent-hover"
            : "border-line-strong bg-surface text-ink hover:bg-raised"
        } ${buttonClassName}`}
      >
        {Icon && (
          <Icon
            className={`h-4 w-4 shrink-0 ${variant === "primary" ? "" : "text-ink-2"}`}
            aria-hidden="true"
          />
        )}
        <span className="truncate">{label}</span>
        {chevron && (
          <ChevronDownIcon
            className={`ms-auto h-4 w-4 shrink-0 ${variant === "primary" ? "" : "text-ink-2"}`}
            aria-hidden="true"
          />
        )}
      </button>

      {open && (
        <div
          className={`absolute top-full z-30 mt-1 max-w-[calc(100vw-2rem)] rounded-xl border border-line bg-surface p-2 shadow-e2 ${align === "end" ? "end-0" : "start-0"} ${panelClassName}`}
        >
          {typeof children === "function" ? children(close) : children}
        </div>
      )}
    </div>
  );
}

/** One row of a menu inside a Popover. */
export function MenuItem({ selected = false, onClick, children, disabled = false }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-start text-dense transition-colors duration-fast disabled:cursor-not-allowed disabled:text-ink-muted ${
        selected ? "bg-accent-wash font-semibold text-accent-text" : "text-ink hover:bg-raised"
      }`}
    >
      {children}
    </button>
  );
}
